<section class="epd-mgmt-card">
  <div class="epd-mgmt-card__header">
    <h2 class="epd-mgmt-card__title">EPD Management</h2>
    <span class="epd-mgmt-card__subtitle">Manage submissions to {{ selectedStandardName }}</span>
  </div>

  @switch (warningState) {
    @case ('noFamily') {
      <ng-container *ngTemplateOutlet="noFamilyWarning"></ng-container>
    }
    @case ('notReferent') {
      <ng-container *ngTemplateOutlet="notReferentWarning"></ng-container>
    }
    @default {
      <ng-container *ngTemplateOutlet="normalContent"></ng-container>
    }
  }
</section>

<ng-template #noFamilyWarning>
  <div class="epd-mgmt-card__warning">
    <div class="epd-mgmt-card__warning-banner">
      <span>
        This Product Group is not linked to any family.
        <!-- <a class="epd-mgmt-card__warning-link" (click)="openNoFamilyDialog()">Click here</a>
        to see effects of initialization. -->
      </span>
    </div>
    <div class="epd-mgmt-card__footer epd-mgmt-card__footer--end"></div>
  </div>
</ng-template>

<ng-template #notReferentWarning>
  <div class="epd-mgmt-card__warning">
    <div class="epd-mgmt-card__warning-banner">
      <span>
        This Product Group is not the referent for its family. EPD status management is available from the referent
        Product Group.
      </span>
    </div>
    <div class="epd-mgmt-card__footer epd-mgmt-card__footer--end">
      <mds-button
        type="primary"
        [icon]="MdsIcon.ARROW_RIGHT"
        [isIconLeft]="false"
        aria-label="Go to referent product"
        (click)="goToReferentProduct()"
      >
        Go to referent product
      </mds-button>
    </div>
  </div>
</ng-template>

<ng-template #normalContent>
  @if (data) {
    <div class="epd-mgmt-card__body">
      <div class="epd-mgmt-card__fields-row">
        <div class="epd-mgmt-card__field epd-mgmt-card__field--status">
          <span class="epd-mgmt-card__label">Internal status</span>
          <mds-tag [text]="data.status" [icon]="statusTag.icon" [type]="statusTag.type"></mds-tag>
        </div>
        <div class="epd-mgmt-card__field">
          <span class="epd-mgmt-card__label">Internal version</span>
          <span class="epd-mgmt-card__value" [class.epd-mgmt-card__value--empty]="displayVersion === '-'">
            {{ displayVersion }}
          </span>
        </div>
        <div class="epd-mgmt-card__field" [class.epd-mgmt-card__field--disabled]="changeStatusMode === 'disabled'">
          <span class="epd-mgmt-card__label">Change internal status to...</span>
          @if (changeStatusMode === 'dropdown') {
            <mds-select-dropdown
              [options]="statusOptions"
              [showSearchHeader]="false"
              [showTable]="false"
              (valueChanged)="onStatusSelected($event)"
            >
              <span class="epd-mgmt-card__dropdown-label">{{ pendingStatus ?? data.status }}</span>
              <mds-icon [key]="MdsIcon.KEYBOARD_ARROW_DOWN" [width]="20" [height]="20"></mds-icon>
            </mds-select-dropdown>
          } @else if (changeStatusMode === 'helper') {
            <span class="epd-mgmt-card__helper-text">{{ helperText }}</span>
          } @else {
            <span class="epd-mgmt-card__helper-text">Available once the EPD is initialized</span>
          }
        </div>
      </div>

      @if (data.status === 'Published') {
        <div class="epd-mgmt-card__fields-row">
          <div class="epd-mgmt-card__field">
            <span class="epd-mgmt-card__label">External status</span>
            @if (data.external_status) {
              <mds-tag
                [text]="data.external_status"
                [icon]="externalStatusTag.icon"
                [type]="externalStatusTag.type"
              ></mds-tag>
            }
            <a class="epd-mgmt-card__refresh-link" (click)="refreshExternalStatus()">
              <mds-icon [key]="MdsIcon.SYNC" [width]="16" [height]="16"></mds-icon>
              {{ externalStatusRefreshText }}
            </a>
          </div>
          <div class="epd-mgmt-card__field">
            <span class="epd-mgmt-card__label">Registration number</span>
            @if (data.registration_number && data.ibu_link) {
              <a
                class="epd-mgmt-card__external-link"
                [href]="data.ibu_link"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{ data.registration_number }}
              </a>
            } @else if (data.registration_number) {
              <span class="epd-mgmt-card__value">{{ data.registration_number }}</span>
            } @else {
              <span class="epd-mgmt-card__value epd-mgmt-card__value--empty">-</span>
            }
          </div>
          <div class="epd-mgmt-card__field">
            <span class="epd-mgmt-card__label">Dataset version</span>
            <span class="epd-mgmt-card__value" [class.epd-mgmt-card__value--empty]="!data.dataset_version">
              {{ data.dataset_version ?? '-' }}
            </span>
          </div>
        </div>

        <div class="epd-mgmt-card__fields-row">
          <div class="epd-mgmt-card__field">
            <span class="epd-mgmt-card__label">Publication date</span>
            <span class="epd-mgmt-card__value" [class.epd-mgmt-card__value--empty]="!data.publication_date">
              @if (data.publication_date) {
                {{ data.publication_date | date: 'dd/MM/yyyy' }}
              } @else {
                -
              }
            </span>
          </div>
          <div class="epd-mgmt-card__field">
            <span class="epd-mgmt-card__label">Valid until</span>
            <span class="epd-mgmt-card__value" [class.epd-mgmt-card__value--empty]="!data.valid_until">
              @if (data.valid_until) {
                {{ data.valid_until | date: 'dd/MM/yyyy' }}
              } @else {
                -
              }
            </span>
          </div>
          <div class="epd-mgmt-card__field">
            <span class="epd-mgmt-card__label">Last revision</span>
            <span class="epd-mgmt-card__value" [class.epd-mgmt-card__value--empty]="!data.last_revision">
              @if (data.last_revision) {
                {{ data.last_revision | date: 'dd/MM/yyyy' }}
              } @else {
                -
              }
            </span>
          </div>
        </div>

        <div class="epd-mgmt-card__fields-row">
          <div class="epd-mgmt-card__field">
            <span class="epd-mgmt-card__label">Last synced</span>
            <span class="epd-mgmt-card__value" [class.epd-mgmt-card__value--empty]="!data.last_synced">
              @if (data.last_synced) {
                {{ data.last_synced | date: "dd/MM/yyyy 'at' HH:mm" }}
              } @else {
                -
              }
            </span>
          </div>
          <div class="epd-mgmt-card__field">
            <span class="epd-mgmt-card__label">
              {{ externalEpdLinkLabel }}
            </span>
            @if (data.ibu_link) {
              <a class="epd-mgmt-card__external-link" [href]="data.ibu_link" target="_blank" rel="noopener noreferrer">
                {{ data.dataset_version ?? 'View' }}
                <mds-icon [key]="MdsIcon.OPEN_IN_NEW" [width]="14" [height]="14"></mds-icon>
              </a>
            } @else {
              <span class="epd-mgmt-card__value epd-mgmt-card__value--empty">-</span>
            }
          </div>
        </div>
      }

      @if (showVerifierRow) {
        <div class="epd-mgmt-card__fields-row">
          <div class="epd-mgmt-card__field">
            <span class="epd-mgmt-card__label">Internal verifier</span>
            <span class="epd-mgmt-card__value" [class.epd-mgmt-card__value--empty]="!data.internal_verifier_name">
              {{ data.internal_verifier_name ?? '-' }}
            </span>
          </div>
          <div class="epd-mgmt-card__field">
            <span class="epd-mgmt-card__label">External reviewer</span>
            <span class="epd-mgmt-card__value" [class.epd-mgmt-card__value--empty]="!data.external_verifier">
              {{ data.external_verifier ?? '-' }}
            </span>
          </div>
        </div>
      }
    </div>

    <div class="epd-mgmt-card__footer">
      <div class="epd-mgmt-card__footer-left">
        @if (showVerifierRow && data.latest_epd_family_user_log) {
          <a class="epd-mgmt-card__notes-link" (click)="openVerifierNotesDialog()">
            <mds-icon [key]="MdsIcon.NOTES" [width]="18" [height]="18"></mds-icon>
            See verifier notes
          </a>
        }
        @if (data.latest_epd_handout_download_link) {
          <a
            class="epd-mgmt-card__notes-link"
            (click)="downloadHandout()"
          >
            <mds-icon [key]="MdsIcon.DOWNLOAD" [width]="18" [height]="18"></mds-icon>
            Download latest handout
          </a>
        }
      </div>
      <div class="epd-mgmt-card__footer-right">
        @for (action of displayedFooterActions; track action.event) {
          <mds-button
            [type]="action.type"
            [icon]="action.icon"
            [isIconLeft]="!action.iconRight"
            [disabled]="action.disabled"
            [attr.aria-label]="action.label"
            (click)="onFooterAction(action.event)"
          >
            {{ action.label }}
          </mds-button>
        }
      </div>
    </div>
  }
</ng-template>

<ng-template #noFamilyDialogBody>
  <div class="epd-mgmt-card__dialog-body">
    <p>This Product Group has no family assigned. A family is required to perform a correct EPD generation process.</p>
    <p>When you click "Initialize EPD", the system automatically:</p>
    <ol>
      <li>Shares the PG with the entire team</li>
      <li>Creates a new taxonomy family</li>
      <li>Adds the PG to the family as the referent product</li>
      <li>Proceeds with normal initialization (handout generation).</li>
    </ol>
    <p>This is a one-time setup per PG.</p>
  </div>
</ng-template>

<ng-template #noFamilyDialogActions>
  <mds-button type="secondary" mat-dialog-close>Understood</mds-button>
</ng-template>

<ng-template #verifierNotesDialogBody>
  <div class="epd-mgmt-card__dialog-body">
    <div class="epd-mgmt-card__dialog-field">
      <span class="epd-mgmt-card__dialog-label">
        {{ isExternalVerifierContext ? 'External reviewer' : 'Internal verifier' }}
      </span>
      <div class="epd-mgmt-card__dialog-verifier-chip">
        <mds-icon [key]="MdsIcon.ADD_PERSON" [width]="16" [height]="16"></mds-icon>
        <span>
          {{ isExternalVerifierContext ? (data?.external_verifier ?? '-') : (data?.internal_verifier_name ?? '-') }}
        </span>
      </div>
    </div>
    <div class="epd-mgmt-card__dialog-field">
      <span class="epd-mgmt-card__dialog-label">Note</span>
      <span class="epd-mgmt-card__dialog-note">{{ data?.latest_epd_family_user_log }}</span>
    </div>
  </div>
</ng-template>

<ng-template #verifierNotesDialogActions>
  <mds-button type="secondary" mat-dialog-close>Close</mds-button>
</ng-template>

<ng-template #confirmStatusDialogActions>
  <mds-button type="secondary" [mat-dialog-close]="false">Cancel</mds-button>
  <mds-button type="primary" [mat-dialog-close]="true">Confirm</mds-button>
</ng-template>
